/**
 * Client-side avatar preparation (#42, FR-2.10).
 *
 * Phone photos are large and often **HEIC** (iPhone camera), which the server can't
 * decode and which blows past the byte cap. So before upload we downscale + re-encode
 * the picked file in the browser: draw it to a canvas at a bounded dimension and export
 * JPEG. This does two things at once —
 *   1. **shrinks** a multi-MB photo to a few hundred KB, and
 *   2. **normalizes the format**: Safari can *decode* HEIC into a canvas, so the
 *      exported bytes are always JPEG regardless of the source (HEIC/PNG/WebP/…).
 * The server then re-encodes that to its own normalized WebP + moderates it.
 *
 * If the browser can't do the canvas round trip (no `createImageBitmap` / `toBlob`,
 * e.g. under jsdom), we fall back to uploading the original bytes — the server still
 * validates, caps, and re-encodes them.
 */

/** Prepared upload payload: base64 image bytes + their content type. */
export interface PreparedAvatar {
  dataBase64: string;
  contentType: string;
}

/**
 * Fit `(w, h)` within a `maxDim`×`maxDim` box, preserving aspect ratio; never
 * upscales. Pure — the piece worth unit-testing.
 */
export function computeScaledSize(
  w: number,
  h: number,
  maxDim: number,
): { width: number; height: number } {
  if (w <= 0 || h <= 0) return { width: 0, height: 0 };
  if (w <= maxDim && h <= maxDim) return { width: w, height: h };
  const scale = maxDim / Math.max(w, h);
  return {
    width: Math.max(1, Math.round(w * scale)),
    height: Math.max(1, Math.round(h * scale)),
  };
}

/** Read a Blob/File into base64 (raw bytes, no data-URL prefix). */
export function blobToBase64(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = String(reader.result);
      const comma = result.indexOf(',');
      resolve(comma >= 0 ? result.slice(comma + 1) : result);
    };
    reader.onerror = () => reject(reader.error ?? new Error('could not read file'));
    reader.readAsDataURL(blob);
  });
}

/**
 * Downscale + re-encode a picked image file for upload. Returns JPEG on the canvas
 * path; falls back to the original file's bytes/type if the browser can't encode.
 */
export async function prepareAvatarUpload(
  file: File,
  maxDim = 1024,
  quality = 0.85,
): Promise<PreparedAvatar> {
  try {
    if (typeof createImageBitmap !== 'function') throw new Error('no createImageBitmap');
    // `from-image` bakes EXIF orientation into the pixels, so the crop is upright even
    // after we drop metadata by re-encoding.
    const bitmap = await createImageBitmap(file, { imageOrientation: 'from-image' });
    const { width, height } = computeScaledSize(bitmap.width, bitmap.height, maxDim);
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext('2d');
    if (!ctx || typeof canvas.toBlob !== 'function') throw new Error('no canvas 2d');
    ctx.drawImage(bitmap, 0, 0, width, height);
    bitmap.close?.();
    const blob = await new Promise<Blob | null>((resolve) =>
      canvas.toBlob(resolve, 'image/jpeg', quality),
    );
    if (!blob) throw new Error('toBlob returned null');
    return { dataBase64: await blobToBase64(blob), contentType: 'image/jpeg' };
  } catch {
    // Fallback: upload the original bytes (server validates + re-encodes + caps).
    return {
      dataBase64: await blobToBase64(file),
      contentType: file.type || 'application/octet-stream',
    };
  }
}
